import { Box, MenuItem, TextField, Typography } from "@mui/material";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import flagUsa from "images/flag-usa.svg";
import flagIreland from "images/flag-ireland.svg";
import flagAustralia from "images/flag-australia.svg";
import flagIndia from "images/flag-india.svg";
import flagSingapore from "images/flag-singapore.png";
import flagUk from "images/flag-uk.svg";
import flagFrance from "images/flag-france.png";
import flagGermany from "images/flag-germany.svg";
import flagSouthKorea from "images/flag-southkorea.svg";
import flagJapan from "images/flags/flag-japan.svg";
import flagCanada from "images/flags/flag-canada.svg";
import flagSweden from "images/flags/flag-sweden.svg";
import flagBrazil from "images/flags/flag-brazil.svg";

// Display metadata only. The bootstrap options API decides which regions are available.
// AWS names: https://docs.aws.amazon.com/global-infrastructure/latest/regions/aws-regions.html
const regions: Record<
  string,
  { name: string; country: string; flag: string } | undefined
> = {
  "ap-northeast-1": {
    name: "Asia Pacific (Tokyo)",
    country: "Japan",
    flag: flagJapan,
  },
  "ap-northeast-2": {
    name: "Asia Pacific (Seoul)",
    country: "South Korea",
    flag: flagSouthKorea,
  },
  "ap-northeast-3": {
    name: "Asia Pacific (Osaka)",
    country: "Japan",
    flag: flagJapan,
  },
  "ap-south-1": {
    name: "Asia Pacific (Mumbai)",
    country: "India",
    flag: flagIndia,
  },
  "ap-southeast-1": {
    name: "Asia Pacific (Singapore)",
    country: "Singapore",
    flag: flagSingapore,
  },
  "ap-southeast-2": {
    name: "Asia Pacific (Sydney)",
    country: "Australia",
    flag: flagAustralia,
  },
  "ca-central-1": {
    name: "Canada (Central)",
    country: "Canada",
    flag: flagCanada,
  },
  "eu-central-1": {
    name: "Europe (Frankfurt)",
    country: "Germany",
    flag: flagGermany,
  },
  "eu-north-1": {
    name: "Europe (Stockholm)",
    country: "Sweden",
    flag: flagSweden,
  },
  "eu-west-1": {
    name: "Europe (Ireland)",
    country: "Ireland",
    flag: flagIreland,
  },
  "eu-west-2": {
    name: "Europe (London)",
    country: "United Kingdom",
    flag: flagUk,
  },
  "eu-west-3": { name: "Europe (Paris)", country: "France", flag: flagFrance },
  "sa-east-1": {
    name: "South America (São Paulo)",
    country: "Brazil",
    flag: flagBrazil,
  },
  "us-east-1": {
    name: "US East (N. Virginia)",
    country: "United States",
    flag: flagUsa,
  },
  "us-east-2": {
    name: "US East (Ohio)",
    country: "United States",
    flag: flagUsa,
  },
  "us-west-2": {
    name: "US West (Oregon)",
    country: "United States",
    flag: flagUsa,
  },
};

function RegionLabel({ region }: { region: string }) {
  const details = regions[region];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
      {details ? (
        <Box
          component="img"
          src={details.flag}
          alt={`${details.country} flag`}
          sx={{ width: 28, height: 21, objectFit: "contain", flexShrink: 0 }}
        />
      ) : (
        <PublicOutlined
          sx={{ width: 28, color: "text.secondary", flexShrink: 0 }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2">{details?.name ?? region}</Typography>
        {details && (
          <Typography variant="caption" color="text.secondary">
            {region}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function HostedRegionSelector({
  availableRegions,
  selectedRegion,
  disabled,
  onSelect,
}: {
  availableRegions: string[];
  selectedRegion: string;
  disabled: boolean;
  onSelect: (region: string) => void;
}) {
  return (
    <TextField
      select
      required
      fullWidth
      size="small"
      label="Cloud region"
      value={selectedRegion}
      disabled={disabled}
      onChange={(event) => onSelect(event.target.value)}
      helperText="Choose where your cluster will be hosted."
      sx={{ maxWidth: 400 }}
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        displayEmpty: true,
        renderValue: (value) =>
          value ? (
            <RegionLabel region={String(value)} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a region
            </Typography>
          ),
        MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
      }}
    >
      <MenuItem value="" disabled>
        Select a region
      </MenuItem>
      {selectedRegion && !availableRegions.includes(selectedRegion) && (
        <MenuItem value={selectedRegion} disabled>
          <RegionLabel region={selectedRegion} />
          <Typography variant="caption" sx={{ ml: 1 }}>
            Unavailable
          </Typography>
        </MenuItem>
      )}
      {availableRegions.map((region) => {
        const details = regions[region];
        return (
          <MenuItem
            key={region}
            value={region}
            aria-label={details ? `${details.name}, ${region}` : region}
          >
            <RegionLabel region={region} />
          </MenuItem>
        );
      })}
    </TextField>
  );
}
